import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { logInfoToDatadog } from '../../../common/utils'
import type { RootState } from '../../../store/redux-store'
import { useWebchatRuntime } from '../use-webchat-runtime'

interface UserIdentity {
  id: string
  applicationId: string
  customerId: string
}

interface ChatSession {
  userId?: string
  conversationId?: string
  webchatToken?: string
  isWebchatRegistered: boolean
}

interface UserData {
  userObj?: UserIdentity
  chatSession: ChatSession
}

export const useUserData = () => {
  const userObj = useSelector((state: RootState) => state.webchat.userObj)
  const userId = useSelector((state: RootState) => state.webchat.userId)
  const conversation = useSelector((state: RootState) => state.webchat.conversation)
  const token = useSelector((state: RootState) => state.webchat.token)
  const runtime = useWebchatRuntime()

  const getUserData = useCallback((): UserData => {
    return {
      userObj,
      chatSession: {
        userId,
        conversationId: conversation,
        webchatToken: token ? '[PRESENT]' : undefined,
        isWebchatRegistered:
          runtime.botConnection !== undefined &&
          conversation !== undefined &&
          runtime.store !== undefined &&
          token !== undefined &&
          userId !== undefined,
      },
    }
  }, [userObj, userId, conversation, token, runtime])

  const logUserData = useCallback(
    (context?: string) => {
      const userData = getUserData()

      const logContext = {
        context: context || 'User data access',
        subjectId: userData.userObj?.id,
        applicationId: userData.userObj?.applicationId,
        customerId: userData.userObj?.customerId,
        userId: userData.chatSession.userId,
        conversationId: userData.chatSession.conversationId,
        hasWebchatToken: userData.chatSession.webchatToken ? 'true' : 'false',
        isWebchatRegistered: userData.chatSession.isWebchatRegistered ? 'true' : 'false',
      }

      logInfoToDatadog('User data accessed', logContext)

      return userData
    },
    [getUserData],
  )

  return {
    getUserData,
    logUserData,
  }
}
